import { endOfDay, parseISO, startOfDay } from 'date-fns';
import jwtDecode from 'jwt-decode';
import { memberApiV2 } from 'lib/api';
import { all, call, put, select } from 'typed-redux-saga';
import {
  calculateEventPositions,
  WCS,
} from '../../../components/ui/calendar/calendar-week/week-utils';
import { mapEvents } from '../../../pages/e-visits/components/calendar-view/calendar-view.utils';
import {
  getInitialPatients,
  updateOrder,
} from '../../../services/e-calendar/reservation-form/reservation-form-service';
import { ReservationFormValues } from '../../../types/reservation-form';
import TStore from '../../../types/store';
import { actions } from '../../ducks/reservation-form-slice';
import {
  getEditedEvent,
  getPatientOptionsFromResponse,
  getTagOptionsFromResponse,
  getTimeRangeOptions,
  getValuesFromDateField,
  handleApiError,
} from './reservation-form-saga.utils';

export function* reservationEdit() {
  try {
    const providerId = yield* select((store: TStore) => store.users.business[0].id);

    yield* put(actions.setFormState('initializing'));
    const { visitItem } = yield* select((state: TStore) => ({
      visitItem: state.reservationForm.visitItem,
    }));

    const { event, order } = visitItem;
    if (!order) {
      throw new Error('Order not found');
    }
    const res = jwtDecode(event.eventId);

    const { eventsResponse, tagOptionsRes, patientOptionsResponse } = yield* all({
      eventsResponse: call(memberApiV2.getEVisitsEvents, providerId, {
        fromDate: startOfDay(parseISO(event.from)),
        toDate: endOfDay(parseISO(event.from)),
        // expertiseCode: event.expertiseCode,
        // medicalInstitutionId: event.medicalInstitutionId,
        // @ts-expect-error lol
        workspaceId: res.workspaceId,
      }),
      tagOptionsRes: call(memberApiV2.getTags),
      patientOptionsResponse: call(getInitialPatients, providerId),
    });

    const eEVisitEvents = eventsResponse.data.data.dates;
    const timeRangeOptions = getTimeRangeOptions(eEVisitEvents, event);
    const positions = calculateEventPositions(
      0,
      [...mapEvents(eEVisitEvents)],
      WCS.gridPadding,
    ).map((positionedEvent) => {
      if (
        positionedEvent.event.eventData?.order &&
        positionedEvent.event.eventData.order.id === order.id
      ) {
        positionedEvent.event.eventData.event.editing = true;
        return positionedEvent;
      } else {
        return positionedEvent;
      }
    });

    yield* put(
      actions.updateReservationForm({
        timeRangeOptions,
        positions: [...positions],
        tagOptions: getTagOptionsFromResponse(tagOptionsRes),
        patientOptions: getPatientOptionsFromResponse(patientOptionsResponse),

        initialValues: {
          selectedPatient: {
            value: order.patientUserId,
            label: order.patientProfile
              ? order.patientProfile.firstName + ' ' + order.patientProfile.lastName
              : '',
            email: 'NOT IMPLEMENTED',
            image: order.patientProfile ? order.patientProfile.profileImage || '' : '',
          },
          selectedTag: order.tag
            ? {
                label: order.tag.name,
                value: order.tag.id,
                color: order.tag.color,
              }
            : null,
          description: event.description || '',
          title: event.title || '',
          // @ts-expect-error changed structure
          extraData: event.extraData,
          timeRangePickerValue: {
            fromTime: event.from,
            toTime: event.to,
          },
          timeRangeSelectValue: timeRangeOptions[0],
          dateField: new Date(event.from).toISOString(),
          patientDescription: order.patientDescription || '',
        },
      }),
    );
    yield* put(actions.setFormState('idle'));
  } catch (error) {
    yield* call(handleApiError, { error });
  }
}

export function* changeReservationFormDate({ payload }: { payload: string }) {
  const providerId = yield* select((store: TStore) => store.users.business[0].id);

  try {
    const { medicalInstitutionId, expertiseCode, event, currentPositions } = yield* select(
      (state: TStore) => ({
        medicalInstitutionId: state.reservationForm.visitItem.event.medicalInstitutionId,
        expertiseCode: state.reservationForm.visitItem.event.expertiseCode,
        event: state.reservationForm.visitItem.event,
        currentPositions: state.reservationForm.positions,
      }),
    );
    console.log('Change');
    const { eventsResponse } = yield* all({
      eventsResponse: call(memberApiV2.getEVisitsEvents, providerId, {
        fromDate: startOfDay(new Date(payload)),
        toDate: endOfDay(new Date(payload)),
        // expertiseCode,
        // medicalInstitutionId,
        workspaceId: medicalInstitutionId,
      }),
    });

    const eEVisitEvents = eventsResponse.data.data.dates;
    const timeRangeOptions = getTimeRangeOptions(eEVisitEvents, event);
    const positions = calculateEventPositions(0, [...mapEvents(eEVisitEvents)], WCS.gridPadding);

    const editedEvent = getEditedEvent(currentPositions);
    yield* put(
      actions.updateReservationForm({
        positions: editedEvent ? [...positions, editedEvent] : [...positions],
        timeRangeOptions,
      }),
    );

    // yield* put(actions.setFormState('submit_success'));
  } catch (error) {
    yield* call(handleApiError, { error });
  }
}

export function* submitReservationFormUpdate({ payload }: { payload: ReservationFormValues }) {
  try {
    const { orderId, positions } = yield* select((state: TStore) => ({
      orderId: state.reservationForm.visitItem.order?.id,
      positions: state.reservationForm.positions,
    }));

    yield* put(actions.setFormState('submitting'));

    /**
     * these values are from DateField
     */
    const { from, to } = getValuesFromDateField(payload.dateField, positions);
    const providerId: string = yield select((state: TStore) => state.users.business[0].id);

    yield* call(updateOrder, providerId, {
      orderId,
      description: payload.description,
      from,
      to,
      eventId: undefined,
      // @ts-expect-error structure change
      clientId: payload.selectedPatient.value,
      title: payload.title,
      tagId: payload.selectedTag?.value || undefined,
    });

    yield* put(actions.setFormState('submit_success'));
  } catch (error) {
    yield* call(handleApiError, { error });
  }
}
